import { InfoboxService } from '@/common/infobox/infobox.service';
import { DatePipe, NgClass } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	inject,
	input,
	OnInit,
	signal,
	viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { filter, finalize, forkJoin, of, switchMap } from 'rxjs';
import { DisplayFile, NewUploadedFile, ApiUploadedFile } from './term-files-section.model';
import { TermFilesApiService } from './api/term-files.api.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RemoveFilesConfirmDialogComponent } from './remove-files-confirm-dialog/remove-files-confirm-dialog.component';
import { TourismGuideFileMultimediaTypesService } from '@/tourist-guides/tourism-guides-file/_services/tourism-guide-file-multimedia-types.service';
import {
	DocumentTypeDialogComponent,
	DocumentTypeDialogData,
	DocumentTypeDialogResult,
} from './document-type-dialog/document-type-dialog.component';
type DisplayFileView = DisplayFile & { documentTypeName: string };

@Component({
	selector: 'app-term-files-section',
	standalone: true,
	imports: [
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatCardModule,
		DatePipe,
		MatProgressSpinnerModule,
		MatButtonToggleModule,
		MatTooltipModule,
		NgClass,
	],
	templateUrl: './term-files-section.component.html',
	styleUrl: './term-files-section.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermFilesSectionComponent implements OnInit {
	readonly termId = input.required<number>();

	readonly loggedUserId = signal<number | null>(null);
	readonly isLoading = signal(false);
	readonly gridStyle = input<'list' | 'grid'>('list');
	readonly externalUrlToAdd = signal('');

	readonly MAX_SIZE_MB = 5;

	readonly displayFiles = computed<DisplayFileView[]>(() => {
		const documentTypes = this.documentTypesService.documentTypes();
		const resolveDocumentTypeName = (documentTypeId: number | null): string =>
			documentTypes.find((type) => type.id === documentTypeId)?.name ?? 'Typ dokumentu';

		return [
			...this.existingFiles().map(
				(file) =>
					({
						...file,
						isNew: false,
						documentTypeName: file.documentType?.name ?? 'Typ dokumentu',
					}) as DisplayFileView,
			),
			...this.newFiles().map(
				(file) =>
					({
						...file,
						isNew: true,
						documentTypeName: resolveDocumentTypeName(file.documentTypeId ?? null),
					}) as DisplayFileView,
			),
		];
	});

	readonly someFileIsInvalid = computed(
		() => !this.newFiles().length || this.newFiles().some((file) => !file.note || !file.documentTypeId),
	);

	private readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

	private readonly newFiles = signal<NewUploadedFile[]>([]);
	private readonly existingFiles = signal<ApiUploadedFile[]>([]);

	private readonly infoboxService = inject(InfoboxService);
	private readonly termFilesApiService = inject(TermFilesApiService);
	private readonly documentTypesService = inject(TourismGuideFileMultimediaTypesService);
	private readonly matDialog = inject(MatDialog);

	ngOnInit(): void {
		this.isLoading.set(true);
		forkJoin({
			files: this.termFilesApiService.getTransportTermFiles(this.termId()),
			profile: this.termFilesApiService.getProfile(),
		})
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe(({ files, profile }) => {
				this.existingFiles.set(files);

				if (profile) {
					this.loggedUserId.set(profile.id);
				}
			});

		this.documentTypesService.load();
	}

	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) {
			return;
		}

		if (file.size > this.MAX_SIZE_MB * 1024 * 1024) {
			this.infoboxService.warning(`Maksymalny rozmiar pliku to ${this.MAX_SIZE_MB}MB`);
			return;
		}

		this.newFiles.update((arr) => [...arr, { file, note: '', externalUrl: '', documentTypeId: null }]);
		this.scrollToBottomSmoothAfterRender();
	}

	addFileWithExternalUrl(): void {
		if (!this.externalUrlToAdd().trim()) {
			return;
		}

		this.newFiles.update((arr) => [
			...arr,
			{ file: '', note: '', externalUrl: this.externalUrlToAdd(), documentTypeId: null },
		]);
		this.externalUrlToAdd.set('');
		this.scrollToBottomSmoothAfterRender();
	}

	onNoteChange(file: DisplayFile, note: string): void {
		this.updateNewFile(file, { note });
	}

	onLinkToGuideFilesChange(file: DisplayFile, linkToGuideFiles: boolean): void {
		this.updateNewFile(file, { linkToGuideFiles });
	}

	onToggleLinkToGuideFiles(file: DisplayFile): void {
		if (file.isNew) {
			if (file.linkToGuideFiles) {
				this.onLinkToGuideFilesChange(file, false);
				return;
			}
			this.openDocumentTypeDialogToLink(file);
			return;
		}

		const existingFile = file as ApiUploadedFile;
		if (existingFile.visibleToPilot) {
			this.toggleLinkToGuideFiles(file);
			return;
		}

		this.openDocumentTypeDialogToLink(file);
	}

	openDocumentTypeDialog(file: DisplayFile): void {
		const selectedId = this.getFileDocumentTypeId(file);

		this.matDialog
			.open<DocumentTypeDialogComponent, DocumentTypeDialogData, DocumentTypeDialogResult>(
				DocumentTypeDialogComponent,
				{
					data: { selectedId },
				},
			)
			.afterClosed()
			.subscribe((result) => {
				if (!result) {
					return;
				}
				if (file.isNew) {
					this.updateNewFile(file, { documentTypeId: result.documentTypeId });
				} else {
					this.onExistingDocumentTypeChange(file, result.documentTypeId);
				}
			});
	}

	removeFile(file: DisplayFile): void {
		if (file.isNew) {
			this.removeNewFile(file);
			return;
		}

		this.removeExistingFile(file as ApiUploadedFile);
	}

	toggleLinkToGuideFiles(displayFile: DisplayFile): void {
		if (displayFile.isNew) {
			return;
		}
		const file = displayFile as ApiUploadedFile;
		const linked = !file.visibleToPilot;
		this.isLoading.set(true);

		this.termFilesApiService
			.updateTransportTermFileVisibility(this.termId(), file.id, linked)
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe((updatedFile) => {
				this.existingFiles.update((files) => files.map((f) => (f.id === file.id ? updatedFile : f)));
			});
	}

	onExistingDocumentTypeChange(displayFile: DisplayFile, documentTypeId: number | null): void {
		if (displayFile.isNew) {
			return;
		}
		const file = displayFile as ApiUploadedFile;
		this.isLoading.set(true);

		this.termFilesApiService
			.updateTransportTermFileDocumentType(this.termId(), file.id, documentTypeId)
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe((updatedFile) => {
				this.existingFiles.update((files) => files.map((f) => (f.id === file.id ? updatedFile : f)));
			});
	}

	save(): void {
		const pendingFiles = this.newFiles();
		const requests = pendingFiles.map((file) =>
			this.termFilesApiService.createTransportTermFile(this.termId(), file),
		);

		if (requests.length === 0) {
			return;
		}

		this.isLoading.set(true);

		forkJoin(requests)
			.pipe(
				switchMap((savedFiles) =>
					forkJoin(
						savedFiles.map((savedFile, index) =>
							pendingFiles[index].linkToGuideFiles
								? this.termFilesApiService.updateTransportTermFileVisibility(
										this.termId(),
										savedFile.id,
										true,
									)
								: of(savedFile),
						),
					),
				),
				finalize(() => this.isLoading.set(false)),
			)
			.subscribe((savedFiles) => {
				this.infoboxService.success('Pliki zapisane pomyślnie');
				const combined = [...this.existingFiles(), ...savedFiles];
				this.existingFiles.set(combined);
				this.newFiles.set([]);
			});
	}

	private openDocumentTypeDialogToLink(file: DisplayFile): void {
		const selectedId = this.getFileDocumentTypeId(file);

		this.matDialog
			.open<DocumentTypeDialogComponent, DocumentTypeDialogData, DocumentTypeDialogResult>(
				DocumentTypeDialogComponent,
				{
					data: { selectedId },
				},
			)
			.afterClosed()
			.subscribe((result) => {
				if (!result) {
					return;
				}
				if (result.documentTypeId === null) {
					this.infoboxService.warning('Aby dołączyć do teczki, wybierz typ dokumentu');
					return;
				}
				if (file.isNew) {
					this.updateNewFile(file, {
						documentTypeId: result.documentTypeId,
						linkToGuideFiles: true,
					});
					return;
				}
				this.linkExistingFileWithDocumentType(file as ApiUploadedFile, result.documentTypeId);
			});
	}

	private linkExistingFileWithDocumentType(file: ApiUploadedFile, documentTypeId: number): void {
		this.isLoading.set(true);

		this.termFilesApiService
			.updateTransportTermFileDocumentType(this.termId(), file.id, documentTypeId)
			.pipe(
				switchMap(() =>
					this.termFilesApiService.updateTransportTermFileVisibility(this.termId(), file.id, true),
				),
				finalize(() => this.isLoading.set(false)),
			)
			.subscribe((updatedFile) => {
				this.existingFiles.update((files) => files.map((f) => (f.id === file.id ? updatedFile : f)));
			});
	}

	private updateNewFile(file: DisplayFile, patch: Partial<NewUploadedFile>): void {
		this.newFiles.update((files) =>
			files.map((f) => {
				const isMatch =
					(file.file instanceof File && f.file === file.file) ||
					(file.externalUrl && f.externalUrl === file.externalUrl);

				return isMatch ? { ...f, ...patch } : f;
			}),
		);
	}

	private getFileDocumentTypeId(displayFile: DisplayFile): number | null {
		if (displayFile.isNew) {
			return displayFile.documentTypeId ?? null;
		}
		const file = displayFile as ApiUploadedFile;
		return file.documentType?.id ?? null;
	}

	private removeNewFile(file: DisplayFile): void {
		this.newFiles.update((files) => files.filter((f) => f.file !== file.file));
	}

	private removeExistingFile(file: ApiUploadedFile): void {
		this.matDialog
			.open(RemoveFilesConfirmDialogComponent)
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => {
				this.isLoading.set(true);

				this.termFilesApiService
					.deleteTransportTermFile(this.termId(), file.id)
					.pipe(finalize(() => this.isLoading.set(false)))
					.subscribe(() => {
						const updated = this.existingFiles().filter((f) => f.id !== file.id);
						this.existingFiles.set(updated);
					});
			});
	}

	private scrollToBottomSmoothAfterRender(): void {
		queueMicrotask(() => {
			requestAnimationFrame(() => {
				const container = this.scrollContainer()?.nativeElement;
				if (container) {
					container.scrollTo({
						top: container.scrollHeight,
						behavior: 'smooth',
					});
				}
			});
		});
	}
}
